<!-- src/components/DonatePopup.vue -->

<template>
    <Teleport to="body">
        <Transition name="donatePopup">
            <div v-if="modelValue" class="donatePopup" role="dialog" aria-modal="true"
                aria-labelledby="donate-popup-title">
                <button class="donatePopup__overlay" type="button" aria-label="Close donation popup"
                    @click="close"></button>

                <section class="donatePopup__panel">
                    <div class="donatePopup__glow donatePopup__glow--one"></div>
                    <div class="donatePopup__glow donatePopup__glow--two"></div>

                    <header class="donatePopup__header">
                        <div>
                            <p class="donatePopup__eyebrow">
                                M.C Bridge of Hope
                            </p>

                            <h2 id="donate-popup-title" class="donatePopup__title">
                                Your kindness matters.
                            </h2>
                        </div>

                        <button class="donatePopup__close" type="button" aria-label="Close donation popup"
                            :disabled="loading" @click="close">
                            <span aria-hidden="true">
                                ×
                            </span>
                        </button>
                    </header>


                    <div class="donatePopup__body">
                        <section class="summary" aria-label="Donation summary">
                            <div class="summary__intro">
                                <p>
                                    Thank you for choosing to support
                                    M.C Bridge of Hope.
                                </p>

                                <small>
                                    Every contribution helps us create
                                    care, connection and opportunity.
                                </small>
                            </div>


                            <div class="summary__amountBlock">
                                <p class="summary__label">
                                    You're giving
                                </p>

                                <div class="summary__amountWrap">
                                    <strong class="summary__amount">
                                        ${{ donationAmount }}
                                    </strong>

                                    <span class="summary__aud">
                                        AUD
                                    </span>
                                </div>

                                <span class="summary__currency">
                                    {{ frequencyLabel }} donation
                                </span>
                            </div>


                            <div class="preset-grid">
                                <button v-for="preset in presetAmounts" :key="preset" type="button" :class="{
                                    active:
                                        donationAmount === preset &&
                                        customAmount === ''
                                }" @click="selectPreset(preset)">
                                    ${{ preset }}
                                </button>
                            </div>


                            <label class="amount-field">
                                <span>
                                    Or enter another amount
                                </span>

                                <div class="amount-input">
                                    <span class="currency">
                                        $
                                    </span>

                                    <input v-model="customAmount" type="number" :min="minAmount" :max="maxAmount"
                                        step="1" placeholder="Enter amount" @input="setCustomAmount" />

                                    <small>
                                        AUD
                                    </small>
                                </div>
                            </label>


                            <label class="message">
                                <div class="message__heading">
                                    <div>
                                        <span>
                                            Leave a message
                                        </span>

                                        <small>
                                            Optional
                                        </small>
                                    </div>

                                    <span class="message__heart">
                                        ♡
                                    </span>
                                </div>

                                <textarea v-model.trim="donationMessage" rows="4" maxlength="450"
                                    placeholder="Share a kind message with your donation..."></textarea>

                                <small class="message__count">
                                    {{ donationMessage.length }}/450
                                </small>
                            </label>


                            <div class="summary__rows">
                                <div class="summary__row">
                                    <span>
                                        Donation
                                    </span>

                                    <strong>
                                        ${{ donationAmount }} AUD
                                    </strong>
                                </div>

                                <div class="summary__row">
                                    <span>
                                        Frequency
                                    </span>

                                    <strong>
                                        {{ frequencyLabel }}
                                    </strong>
                                </div>

                                <div class="summary__row">
                                    <span>
                                        Payment
                                    </span>

                                    <strong>
                                        Stripe Checkout
                                    </strong>
                                </div>
                            </div>


                            <p v-if="amountError" class="summary__error" role="alert">
                                {{ amountError }}
                            </p>

                            <p v-if="serverError" class="summary__error" role="alert">
                                {{ serverError }}
                            </p>


                            <button class="summary__btn" type="button" :disabled="loading" @click="startCheckout">
                                <span v-if="loading">
                                    Taking you to secure payment...
                                </span>

                                <template v-else>
                                    Donate ${{ donationAmount }}

                                    <span>
                                        →
                                    </span>
                                </template>
                            </button>


                            <div class="summary__security">
                                <div class="summary__securityIcon">
                                    ✓
                                </div>

                                <div>
                                    <strong>
                                        Safe & secure payment
                                    </strong>

                                    <small>
                                        Payments are securely processed
                                        through Stripe Checkout.
                                    </small>
                                </div>
                            </div>


                            <div class="summary__footer">
                                <p>
                                    Prefer another way to support our work?
                                </p>

                                <RouterLink to="/contact" class="summary__link" @click="close">
                                    Get in touch →
                                </RouterLink>
                            </div>
                        </section>
                    </div>
                </section>
            </div>
        </Transition>
    </Teleport>
</template>


<script>
import axios from "axios";

export default {
    name: "DonatePopup",

    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },

        amount: {
            type: Number,
            default: 50,
        },

        frequency: {
            type: String,
            default: "one_off",
        },

        minAmount: {
            type: Number,
            default: 20,
        },

        maxAmount: {
            type: Number,
            default: 5000,
        },
    },

    emits: [
        "update:modelValue",
    ],

    data() {
        return {
            donationAmount: 50,
            customAmount: "",
            donationMessage: "",

            loading: false,
            amountError: "",
            serverError: "",

            presetAmounts: [
                20,
                50,
                100,
                250,
                500,
            ],
        };
    },

    computed: {
        frequencyLabel() {
            if (this.frequency === "weekly") {
                return "Weekly";
            }

            if (this.frequency === "fortnightly") {
                return "Fortnightly";
            }

            if (this.frequency === "monthly") {
                return "Monthly";
            }

            return "One-off";
        },
    },

    watch: {
        modelValue(value) {
            this.clearErrors();

            if (typeof document !== "undefined") {
                document.body.style.overflow =
                    value
                        ? "hidden"
                        : "";
            }

            if (value) {
                this.setStartingAmount();
            }

            if (!value) {
                this.donationMessage = "";
            }
        },

        amount() {
            if (this.modelValue) {
                this.setStartingAmount();
            }
        },
    },

    mounted() {
        this.setStartingAmount();

        if (
            this.modelValue &&
            typeof document !== "undefined"
        ) {
            document.body.style.overflow =
                "hidden";
        }
    },

    beforeUnmount() {
        if (typeof document !== "undefined") {
            document.body.style.overflow =
                "";
        }
    },

    methods: {
        setStartingAmount() {
            const amount =
                Number(this.amount);

            this.donationAmount =
                Number.isFinite(amount)
                    ? Math.round(amount)
                    : 50;

            this.customAmount = "";
        },

        close() {
            if (this.loading) {
                return;
            }

            this.$emit(
                "update:modelValue",
                false,
            );
        },

        clearErrors() {
            this.amountError = "";
            this.serverError = "";
        },

        selectPreset(amount) {
            this.donationAmount = amount;
            this.customAmount = "";
            this.amountError = "";
        },

        setCustomAmount() {
            this.amountError = "";

            if (this.customAmount === "") {
                return;
            }

            const amount =
                Number(this.customAmount);

            if (Number.isFinite(amount)) {
                this.donationAmount =
                    Math.round(amount);
            }
        },

        validate() {
            if (
                !this.donationAmount ||
                this.donationAmount < this.minAmount
            ) {
                this.amountError =
                    `Minimum donation is $${this.minAmount}.`;

                return false;
            }

            if (
                this.donationAmount > this.maxAmount
            ) {
                this.amountError =
                    `Maximum donation is $${this.maxAmount}.`;

                return false;
            }

            return true;
        },

        buildDonationPayload() {
            return {
                amountAud:
                    this.donationAmount,

                amount:
                    this.donationAmount,

                currency:
                    "aud",

                frequency:
                    this.frequency,

                donorName:
                    "",

                donorEmail:
                    "",

                message:
                    this.donationMessage,

                donorMessage:
                    this.donationMessage,

                campaignName:
                    "M.C Bridge of Hope",

                campaignId:
                    "mary-chan-bridge-of-hope",
            };
        },

        async startCheckout() {
            this.clearErrors();

            if (!this.validate()) {
                return;
            }

            this.loading = true;

            try {
                const { data } =
                    await axios.post(
                        // "http://localhost:3000/marychan/donate",
                        "https://my-app-two-1d26224db3b5.herokuapp.com/marychan/donate",

                        this.buildDonationPayload(),
                    );

                if (
                    !data?.success ||
                    !data?.url
                ) {
                    throw new Error(
                        data?.error ||
                        "Could not start donation checkout.",
                    );
                }

                window.location.assign(
                    data.url,
                );
            } catch (error) {
                this.serverError =
                    error.response?.data?.error ||
                    error.message ||
                    "Something went wrong starting checkout.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>


<style scoped lang="scss">
.donatePopup {
    --navy: #07384d;
    --navy-deep: #042b3c;
    --navy-soft: #0d526d;
    --gold: #c5a36d;
    --gold-light: #e4cfaa;
    --cream: #f8f1e5;
    --cream-soft: #fffaf2;
    --paper: #ffffff;
    --text: #183039;
    --muted: #728086;
    --border: rgba(7, 56, 77, 0.11);
    --danger: #991b1b;

    position: fixed;
    inset: 0;
    z-index: 9999;

    display: grid;
    align-items: end;

    padding: 10px;

    @media (min-width: 760px) {
        place-items: center;

        padding: 22px;
    }

    @media (min-width: 1200px) {
        padding: 30px;
    }


    .donatePopup__overlay {
        position: absolute;
        inset: 0;

        border: 0;

        background:
            radial-gradient(700px 420px at 14% 8%,
                rgba(197, 163, 109, 0.18),
                transparent 65%),
            radial-gradient(800px 460px at 88% 12%,
                rgba(13, 82, 109, 0.23),
                transparent 65%),
            rgba(4, 28, 39, 0.68);

        backdrop-filter:
            blur(12px);

        cursor: pointer;
    }


    .donatePopup__panel {
        position: relative;
        z-index: 1;

        width: 100%;
        max-height: 94vh;

        overflow: hidden;

        border:
            1px solid rgba(255, 255, 255, 0.35);

        border-radius:
            26px 26px 18px 18px;

        background:
            var(--paper);

        box-shadow:
            0 34px 100px rgba(0, 0, 0, 0.28);

        @media (min-width: 760px) {
            width:
                min(500px, 100%);

            max-height: 92vh;

            border-radius: 30px;
        }

        @media (min-width: 1200px) {
            width:
                min(520px, 100%);

            border-radius: 34px;
        }
    }


    .donatePopup__glow {
        position: absolute;

        border-radius: 50%;

        pointer-events: none;

        &.donatePopup__glow--one {
            top: -120px;
            right: -100px;

            width: 260px;
            height: 260px;

            background:
                rgba(197, 163, 109, 0.15);

            @media (min-width: 760px) {
                top: -140px;
                right: -120px;

                width: 300px;
                height: 300px;
            }

            @media (min-width: 1200px) {
                width: 330px;
                height: 330px;
            }
        }

        &.donatePopup__glow--two {
            bottom: -150px;
            left: -130px;

            width: 280px;
            height: 280px;

            background:
                rgba(13, 82, 109, 0.08);

            @media (min-width: 760px) {
                width: 300px;
                height: 300px;
            }

            @media (min-width: 1200px) {
                width: 340px;
                height: 340px;
            }
        }
    }


    .donatePopup__header {
        position: relative;
        z-index: 2;

        display: flex;
        justify-content: space-between;
        align-items: flex-start;

        gap: 15px;

        padding: 20px;

        border-bottom:
            1px solid var(--border);

        background:
            rgba(255, 255, 255, 0.93);

        backdrop-filter:
            blur(14px);

        @media (min-width: 760px) {
            padding: 23px 25px;
        }

        @media (min-width: 1200px) {
            padding: 25px 28px;
        }
    }


    .donatePopup__eyebrow {
        margin: 0 0 7px;

        color: var(--gold);

        font-size: 9px;
        font-weight: 900;

        letter-spacing: 0.17em;
        text-transform: uppercase;

        @media (min-width: 760px) {
            font-size: 10px;
        }
    }


    .donatePopup__title {
        margin: 0;

        color: var(--navy);

        font-size:
            clamp(25px, 7vw, 32px);

        line-height: 1.05;
        font-weight: 950;

        letter-spacing: -0.035em;

        @media (min-width: 760px) {
            font-size:
                clamp(28px, 5vw, 35px);
        }

        @media (min-width: 1200px) {
            font-size: 36px;
        }
    }


    .donatePopup__close {
        display: grid;
        place-items: center;

        width: 39px;
        height: 39px;

        flex: 0 0 auto;

        appearance: none;

        border:
            1px solid var(--border);

        border-radius: 50%;

        color: var(--navy);

        background:
            #ffffff;

        cursor: pointer;

        font-family: inherit;
        font-size: 27px;
        line-height: 1;

        transition:
            transform 150ms ease,
            box-shadow 150ms ease;

        &:hover {
            transform:
                rotate(7deg);

            box-shadow:
                0 8px 20px rgba(4, 43, 60, 0.12);
        }

        &:disabled {
            cursor: not-allowed;

            opacity: 0.5;
        }

        span {
            position: relative;

            top: -1px;
        }
    }


    .donatePopup__body {
        position: relative;
        z-index: 2;

        max-height:
            calc(94vh - 82px);

        overflow-y: auto;

        overscroll-behavior:
            contain;

        @media (min-width: 760px) {
            max-height:
                calc(92vh - 86px);
        }
    }


    .summary {
        padding: 19px;

        color: var(--text);

        @media (min-width: 760px) {
            padding: 24px;
        }

        @media (min-width: 1200px) {
            padding: 26px;
        }
    }


    .summary__intro {
        margin-bottom: 17px;

        p {
            margin: 0;

            color: var(--navy);

            font-size: 12px;
            font-weight: 800;
            line-height: 1.5;
        }

        small {
            display: block;

            margin-top: 4px;

            color: var(--muted);

            font-size: 9px;
            line-height: 1.5;
        }

        @media (min-width: 760px) {
            p {
                font-size: 13px;
            }

            small {
                font-size: 10px;
            }
        }
    }


    .summary__amountBlock {
        margin-bottom: 17px;
        padding: 20px;

        border:
            1px solid rgba(197, 163, 109, 0.18);

        border-radius: 20px;

        background:
            radial-gradient(280px 160px at 100% 0%,
                rgba(197, 163, 109, 0.16),
                transparent 62%),
            linear-gradient(145deg,
                var(--cream),
                var(--cream-soft));

        @media (min-width: 760px) {
            padding: 23px;

            border-radius: 22px;
        }
    }


    .summary__label {
        margin: 0 0 6px;

        color: var(--muted);

        font-size: 9px;
        font-weight: 900;

        letter-spacing: 0.11em;
        text-transform: uppercase;
    }


    .summary__amountWrap {
        display: flex;
        align-items: flex-end;

        gap: 8px;
    }


    .summary__amount {
        color: var(--navy);

        font-size:
            clamp(46px, 15vw, 58px);

        line-height: 0.95;
        font-weight: 950;

        letter-spacing: -0.055em;

        @media (min-width: 760px) {
            font-size: 62px;
        }
    }


    .summary__aud {
        padding-bottom: 5px;

        color: var(--muted);

        font-size: 9px;
        font-weight: 900;
    }


    .summary__currency {
        display: block;

        margin-top: 7px;

        color: var(--muted);

        font-size: 10px;
        font-weight: 700;
    }


    .preset-grid {
        display: grid;

        grid-template-columns:
            repeat(3, 1fr);

        gap: 7px;

        margin-bottom: 17px;

        button {
            min-height: 43px;

            border:
                1px solid var(--border);

            border-radius: 12px;

            color: var(--navy);
            background: #ffffff;

            cursor: pointer;

            font-family: inherit;
            font-size: 11px;
            font-weight: 900;

            transition:
                transform 150ms ease,
                background 150ms ease,
                color 150ms ease;

            &:hover {
                transform:
                    translateY(-1px);

                background:
                    var(--cream-soft);
            }

            &.active {
                color: #ffffff;

                border-color:
                    var(--navy);

                background:
                    var(--navy);
            }
        }

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(5, 1fr);

            button {
                font-size: 12px;
            }
        }
    }


    .amount-field {
        display: block;

        margin-bottom: 18px;

        >span {
            display: block;

            margin-bottom: 7px;

            color: var(--muted);

            font-size: 9px;
            font-weight: 900;

            letter-spacing: 0.05em;
            text-transform: uppercase;
        }
    }


    .amount-input {
        display: grid;

        grid-template-columns:
            42px 1fr auto;

        align-items: center;

        overflow: hidden;

        border:
            1px solid var(--border);

        border-radius: 13px;

        background:
            #ffffff;

        .currency {
            display: grid;
            place-items: center;

            height: 49px;

            border-right:
                1px solid var(--border);

            color: var(--navy);

            font-weight: 900;
        }

        input {
            width: 100%;
            height: 49px;

            box-sizing: border-box;

            padding: 0 12px;

            border: 0;
            outline: 0;

            color: var(--text);

            background:
                transparent;

            font: inherit;
            font-size: 12px;
            font-weight: 800;
        }

        small {
            padding-right: 13px;

            color: var(--muted);

            font-size: 9px;
            font-weight: 900;
        }
    }


    .message {
        display: block;

        margin-bottom: 18px;
        padding: 14px;

        border-radius: 17px;

        background:
            #faf8f4;

        textarea {
            width: 100%;
            min-height: 92px;

            box-sizing: border-box;

            padding: 13px;

            resize: vertical;

            border:
                1px solid var(--border);

            border-radius: 12px;

            outline: none;

            color: var(--text);

            background:
                #ffffff;

            font: inherit;
            font-size: 11px;
            line-height: 1.55;

            &:focus {
                border-color:
                    var(--gold);

                box-shadow:
                    0 0 0 3px rgba(197, 163, 109, 0.1);
            }

            @media (min-width: 760px) {
                min-height: 100px;

                font-size: 12px;
            }
        }

        @media (min-width: 760px) {
            padding: 16px;
        }
    }


    .message__heading {
        display: flex;
        justify-content: space-between;
        align-items: center;

        margin-bottom: 8px;

        >div {

            span,
            small {
                display: block;
            }

            span {
                color: var(--navy);

                font-size: 10px;
                font-weight: 900;
            }

            small {
                margin-top: 2px;

                color: var(--muted);

                font-size: 8px;
            }
        }
    }


    .message__heart {
        color: var(--gold);

        font-size: 22px;
    }


    .message__count {
        display: block;

        margin-top: 5px;

        color: var(--muted);

        font-size: 8px;

        text-align: right;
    }


    .summary__rows {
        display: grid;

        margin-bottom: 18px;
    }


    .summary__row {
        display: flex;
        justify-content: space-between;
        align-items: center;

        gap: 15px;

        padding: 11px 2px;

        border-bottom:
            1px solid var(--border);

        color: var(--muted);

        font-size: 10px;

        strong {
            color: var(--navy);

            font-size: 10px;
            font-weight: 900;

            text-align: right;
        }

        @media (min-width: 760px) {
            padding: 12px 2px;

            font-size: 11px;

            strong {
                font-size: 11px;
            }
        }
    }


    .summary__error {
        margin: 0 0 12px;

        padding: 9px 11px;

        border-radius: 10px;

        color: var(--danger);

        background:
            #fff4f4;

        font-size: 10px;
        font-weight: 700;
        line-height: 1.45;

        text-align: center;
    }


    .summary__btn {
        display: flex;
        justify-content: center;
        align-items: center;

        gap: 8px;

        width: 100%;
        min-height: 53px;

        padding: 12px 17px;

        appearance: none;

        border: 0;
        border-radius: 999px;

        color: #ffffff;

        background:
            linear-gradient(135deg,
                var(--navy-soft),
                var(--navy-deep));

        cursor: pointer;

        font-family: inherit;
        font-size: 12px;
        font-weight: 900;

        box-shadow:
            0 12px 28px rgba(4, 43, 60, 0.18);

        transition:
            transform 150ms ease,
            box-shadow 150ms ease;

        &:hover {
            transform:
                translateY(-1px);

            box-shadow:
                0 15px 32px rgba(4, 43, 60, 0.22);
        }

        &:disabled {
            cursor: wait;

            opacity: 0.65;

            transform: none;

            box-shadow: none;
        }

        @media (min-width: 760px) {
            min-height: 55px;

            font-size: 13px;
        }
    }


    .summary__security {
        display: flex;
        align-items: center;

        gap: 9px;

        margin-top: 14px;

        padding: 11px 12px;

        border:
            1px solid var(--border);

        border-radius: 12px;

        background:
            rgba(248, 241, 229, 0.45);

        strong,
        small {
            display: block;
        }

        strong {
            color: var(--navy);

            font-size: 9px;
        }

        small {
            margin-top: 2px;

            color: var(--muted);

            font-size: 8px;
            line-height: 1.4;
        }

        @media (min-width: 760px) {
            padding: 12px 14px;

            strong {
                font-size: 10px;
            }

            small {
                font-size: 9px;
            }
        }
    }


    .summary__securityIcon {
        display: grid;
        place-items: center;

        width: 28px;
        height: 28px;

        flex: 0 0 auto;

        border-radius: 50%;

        color: #ffffff;

        background:
            var(--navy);

        font-size: 11px;
        font-weight: 900;
    }


    .summary__footer {
        margin-top: 17px;
        padding-top: 15px;

        border-top:
            1px solid var(--border);

        text-align: center;

        p {
            margin: 0 0 5px;

            color: var(--muted);

            font-size: 9px;
        }
    }


    .summary__link {
        color: var(--navy);

        font-size: 9px;
        font-weight: 900;

        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }

        @media (min-width: 760px) {
            font-size: 10px;
        }
    }
}


.donatePopup-enter-active,
.donatePopup-leave-active {
    transition:
        opacity 180ms ease;

    .donatePopup__panel {
        transition:
            transform 220ms ease,
            opacity 220ms ease;
    }
}


.donatePopup-enter-from,
.donatePopup-leave-to {
    opacity: 0;

    .donatePopup__panel {
        opacity: 0;

        transform:
            translateY(18px) scale(0.97);
    }
}


@media (prefers-reduced-motion: reduce) {

    .donatePopup-enter-active,
    .donatePopup-leave-active,
    .donatePopup__panel,
    .donatePopup__close,
    .summary__btn {
        transition:
            none !important;
    }
}
</style>